import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_ESA_OFFICE, API_TIMEOUT } from "utils";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import MDProgress from "components/MDProgress";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 2 PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

// Utils
import { getTimeKu } from "utils";

function KinerjaAllDinas() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // State management
  const [loading, setLoading] = useState(false);
  const [tanggalAwal, setTanggalAwal] = useState(() => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const [tanggalAkhir, setTanggalAkhir] = useState(() => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });

  // Data states
  const [listLaporPD, setListLaporPD] = useState([]);
  const [hariEfektif, setHariEfektif] = useState(null);
  const [jumlahAsn, setJumlahAsn] = useState(0);
  const [kehadiran, setKehadiran] = useState(0);
  const [dateTimeKu, setDateTimeKu] = useState("");

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: "performance",
    direction: "desc", // Default: tertinggi ke terendah
  });

  // Helper functions
  const ambilTanggal = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const capitalizeWords = (str) => {
    if (!str) return "";
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getTime = () => {
    const data = getTimeKu();
    setDateTimeKu(data.datetime);
  };

  // API call to get laporan PD
  const getLaporanPD = async (data) => {
    setLoading(true);
    try {
      const response = await axios({
        method: "post",
        url: "https://smartservice.rotendaokab.go.id/api/admin/unitkerja/dho/range",
        timeout: API_TIMEOUT,
        data: data,
        headers: {
          Authorization: `Bearer ${""}`,
        },
      });

      if (response.status === 200) {
        const result = response.data.data;
        const hariEfektifData = response.data.hariEfektif;
        setHariEfektif(hariEfektifData);

        // Filter data where posisi == 0
        const filter = result.filter((item) => item.posisi === 0);

        let rerata = 0;
        let jumlahASN = 0;
        filter.forEach((item) => {
          jumlahASN += item.jumlah_asn;
          rerata +=
            item.Hadir +
            item.Dinas_Luar +
            item.Tugas_Belajar +
            item.Izin_Belajar;
        });

        setJumlahAsn(jumlahASN);
        setKehadiran(Math.round(rerata));
        setListLaporPD(filter);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      alert("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const loadingData = () => {
    setListLaporPD([]);
    const data = {
      tanggalawal: tanggalAwal,
      tanggalakhir: tanggalAkhir,
    };
    getLaporanPD(data);
  };

  // Effects
  useEffect(() => {
    getTime();
  }, []);

  useEffect(() => {
    if (tanggalAwal && tanggalAkhir) {
      loadingData();
    }
  }, []);

  // Calculate performance percentage
  const calculatePerformance = (item) => {
    if (!item.jumlah_asn) return 0;
    const totalKehadiran =
      item.Hadir + item.Dinas_Luar + item.Tugas_Belajar + item.Izin_Belajar;
    return Math.round((totalKehadiran / item.jumlah_asn) * 100);
  };

  // Calculate tidak mengisi percentage
  const calculateTidakMengisi = (item) => {
    if (!item.jumlah_asn) return 0;
    const totalKehadiran =
      item.Hadir + item.Dinas_Luar + item.Tugas_Belajar + item.Izin_Belajar;
    const totalIzin = item.Izin + item.Sakit + item.Cuti;
    const tidakMengisi = item.jumlah_asn - (totalKehadiran + totalIzin);
    return Math.round((tidakMengisi / item.jumlah_asn) * 100);
  };

  // Calculate performance value for progress bar (0-100)
  const calculatePerformanceValue = (item) => {
    if (!item.jumlah_asn) return 0;
    const totalKehadiran =
      item.Hadir + item.Dinas_Luar + item.Tugas_Belajar + item.Izin_Belajar;
    return Math.min(Math.round((totalKehadiran / item.jumlah_asn) * 100), 100);
  };

  // Calculate tidak mengisi value for progress bar (0-100)
  const calculateTidakMengisiValue = (item) => {
    if (!item.jumlah_asn) return 0;
    const totalKehadiran =
      item.Hadir + item.Dinas_Luar + item.Tugas_Belajar + item.Izin_Belajar;
    const totalIzin = item.Izin + item.Sakit + item.Cuti;
    const tidakMengisi = item.jumlah_asn - (totalKehadiran + totalIzin);
    return Math.min(Math.round((tidakMengisi / item.jumlah_asn) * 100), 100);
  };

  // DataTable columns - memoized to prevent re-rendering
  const columns = useMemo(
    () => [
      { Header: "No", accessor: "no", width: "5%" },
      {
        Header: "Perangkat Daerah",
        accessor: "unitKerja",
        width: "200px",
        minWidth: "200px",
        maxWidth: "200px",
      },
      { Header: "Hari Kerja", accessor: "hariKerja", width: "8%" },
      { Header: "Jumlah ASN", accessor: "jumlah_asn", width: "8%" },
      { Header: "Hadir", accessor: "Hadir", width: "6%" },
      { Header: "Dinas Luar", accessor: "Dinas_Luar", width: "8%" },
      { Header: "Izin", accessor: "Izin", width: "6%" },
      { Header: "Sakit", accessor: "Sakit", width: "6%" },
      { Header: "Cuti", accessor: "Cuti", width: "6%" },
      { Header: "Tugas Belajar", accessor: "Tugas_Belajar", width: "8%" },
      { Header: "Izin Belajar", accessor: "Izin_Belajar", width: "8%" },
      {
        Header: "Performance",
        accessor: "performance",
        width: "10%",
        sortable: true,
        sortValue: (row) => calculatePerformance(row.original),
      },
      { Header: "Tidak Mengisi", accessor: "tidakMengisi", width: "10%" },
      { Header: "Aksi", accessor: "aksi", width: "8%" },
    ],
    []
  );

  // Sorting function
  const sortData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      if (sortConfig.key === "performance") {
        aValue = calculatePerformance(a);
        bValue = calculatePerformance(b);
      } else {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  // Handle sorting change
  const handleSortChange = (newDirection) => {
    setSortConfig({
      key: "performance",
      direction: newDirection,
    });
  };

  // DataTable rows - memoized to prevent re-rendering with sorting
  const rows = useMemo(() => {
    const sortedData = sortData(listLaporPD);
    return sortedData.map((item, index) => ({
      no: index + 1,
      unitKerja: (
        <MDBox
          sx={{
            wordWrap: "break-word",
            wordBreak: "break-word",
            whiteSpace: "normal",
            lineHeight: 1.4,
            maxWidth: "200px",
            minWidth: "200px",
            width: "200px",
          }}
        >
          <MDTypography
            variant="caption"
            color="text"
            fontWeight="medium"
            sx={{
              wordWrap: "break-word",
              wordBreak: "break-word",
              whiteSpace: "normal",
              lineHeight: 1.4,
              display: "block",
            }}
          >
            {capitalizeWords(item.unitKerja)}
          </MDTypography>
        </MDBox>
      ),
      hariKerja: (
        <Chip
          label={item.hariKerja === 0 ? "5" : "6"}
          color={item.hariKerja === 0 ? "primary" : "success"}
          size="small"
        />
      ),
      jumlah_asn: item.jumlah_asn,
      Hadir: Math.round(item.Hadir),
      Dinas_Luar: Math.round(item.Dinas_Luar),
      Izin: Math.round(item.Izin),
      Sakit: Math.round(item.Sakit),
      Cuti: Math.round(item.Cuti),
      Tugas_Belajar: Math.round(item.Tugas_Belajar),
      Izin_Belajar: Math.round(item.Izin_Belajar),
      performance: (
        <MDBox>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {calculatePerformance(item)}% kehadiran
          </MDTypography>
          <MDProgress
            variant="determinate"
            value={calculatePerformanceValue(item)}
            color={calculatePerformanceValue(item) > 50 ? "success" : "error"}
            sx={{ mt: 0.5 }}
          />
        </MDBox>
      ),
      tidakMengisi: (
        <MDBox>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {calculateTidakMengisi(item)}% tidak mengisi
          </MDTypography>
          <MDProgress
            variant="determinate"
            value={calculateTidakMengisiValue(item)}
            color={calculateTidakMengisiValue(item) < 50 ? "success" : "error"}
            sx={{ mt: 0.5 }}
          />
        </MDBox>
      ),
      aksi: (
        <MDButton
          variant="outlined"
          color="warning"
          size="small"
          onClick={() => {
            navigate(
              `/pages/smartDH/kinerja-alldinas/detail/${"1"}?tAwal=${tanggalAwal}&tAkhir=${tanggalAkhir}&unitKerja=${
                item.unitKerja
              }`
            );
          }}
        >
          <Icon>visibility</Icon>&nbsp;Detail
        </MDButton>
      ),
    }));
  }, [listLaporPD, tanggalAwal, tanggalAkhir, navigate, sortConfig]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            {/* Dashboard Cards */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <MDBox display="flex" alignItems="center">
                    <MDBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="3rem"
                      height="3rem"
                      borderRadius="md"
                      color="info"
                      bgColor="info"
                      variant="gradient"
                      mr={2}
                    >
                      <Icon fontSize="small">calendar_today</Icon>
                    </MDBox>
                    <MDBox>
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="medium"
                      >
                        Hari Kerja
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="bold">
                        {hariEfektif?.hariEfektif5?.length || 0}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <MDBox display="flex" alignItems="center">
                    <MDBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="3rem"
                      height="3rem"
                      borderRadius="md"
                      color="success"
                      bgColor="success"
                      variant="gradient"
                      mr={2}
                    >
                      <Icon fontSize="small">people</Icon>
                    </MDBox>
                    <MDBox>
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="medium"
                      >
                        Jumlah ASN
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="bold">
                        {jumlahAsn}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <MDBox display="flex" alignItems="center">
                    <MDBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="3rem"
                      height="3rem"
                      borderRadius="md"
                      color="warning"
                      bgColor="warning"
                      variant="gradient"
                      mr={2}
                    >
                      <Icon fontSize="small">trending_up</Icon>
                    </MDBox>
                    <MDBox>
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="medium"
                      >
                        Rerata Kehadiran
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="bold">
                        {kehadiran}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <MDBox>
                    <MDTypography
                      variant="button"
                      color="text"
                      fontWeight="medium"
                    >
                      Persentase Kehadiran
                    </MDTypography>
                    <MDTypography variant="h6" fontWeight="bold">
                      {jumlahAsn > 0
                        ? Math.round((kehadiran / jumlahAsn) * 100)
                        : 0}
                      %
                    </MDTypography>
                    <MDProgress
                      variant="determinate"
                      value={jumlahAsn > 0 ? (kehadiran / jumlahAsn) * 100 : 0}
                      color={
                        jumlahAsn > 0 && (kehadiran / jumlahAsn) * 100 > 50
                          ? "success"
                          : "error"
                      }
                      sx={{ mt: 1 }}
                    />
                  </MDBox>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        {/* Filter Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Filter Data
            </MDTypography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <MDInput
                  type="date"
                  label="Tanggal Awal"
                  value={tanggalAwal}
                  onChange={(e) => setTanggalAwal(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <MDInput
                  type="date"
                  label="Tanggal Akhir"
                  value={tanggalAkhir}
                  onChange={(e) => setTanggalAkhir(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={loadingData}
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Icon>refresh</Icon>
                    )
                  }
                >
                  {loading ? "Loading..." : "Load Data"}
                </MDButton>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Data Table / Mobile Cards */}
        <Card>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={3}
          >
            <MDTypography variant="h6" fontWeight="medium">
              Perangkat Daerah Kab. Rote Ndao
            </MDTypography>
            <MDButton
              variant="outlined"
              color="success"
              size="small"
              startIcon={<Icon>download</Icon>}
            >
              Export Excel
            </MDButton>
          </MDBox>
          <MDBox p={isMobile ? 2 : 0}>
            {isMobile ? (
              <MDBox display="flex" flexDirection="column" gap={1}>
                {loading && (
                  <MDBox display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={20} />
                  </MDBox>
                )}
                {!loading &&
                  sortData(listLaporPD).map((item, index) => {
                    const perfPct = calculatePerformance(item);
                    const perfVal = calculatePerformanceValue(item);
                    const tdkPct = calculateTidakMengisi(item);
                    const tdkVal = calculateTidakMengisiValue(item);
                    return (
                      <Card key={`${item.unitKerja || "row"}-${index}`}>
                        <CardContent>
                          <MDTypography variant="button" fontWeight="bold">
                            {index + 1}. {capitalizeWords(item.unitKerja)}
                          </MDTypography>
                          <MDBox
                            mt={0.75}
                            display="flex"
                            flexWrap="wrap"
                            gap={0.5}
                          >
                            <Chip
                              label={`Hari Kerja: ${
                                item.hariKerja === 0 ? "5" : "6"
                              }`}
                              color={
                                item.hariKerja === 0 ? "primary" : "success"
                              }
                              size="small"
                            />
                            <Chip
                              label={`ASN: ${item.jumlah_asn}`}
                              color="info"
                              size="small"
                            />
                            <Chip
                              label={`Hadir: ${Math.round(item.Hadir)}`}
                              color="success"
                              size="small"
                            />
                            <Chip
                              label={`Dinas Luar: ${Math.round(
                                item.Dinas_Luar
                              )}`}
                              color="info"
                              size="small"
                            />
                            <Chip
                              label={`Izin: ${Math.round(item.Izin)}`}
                              color="warning"
                              size="small"
                            />
                            <Chip
                              label={`Sakit: ${Math.round(item.Sakit)}`}
                              color="error"
                              size="small"
                            />
                            <Chip
                              label={`Cuti: ${Math.round(item.Cuti)}`}
                              size="small"
                            />
                            <Chip
                              label={`Tugas Belajar: ${Math.round(
                                item.Tugas_Belajar
                              )}`}
                              color="secondary"
                              size="small"
                            />
                            <Chip
                              label={`Izin Belajar: ${Math.round(
                                item.Izin_Belajar
                              )}`}
                              color="default"
                              size="small"
                            />
                          </MDBox>
                          <MDBox mt={1.25}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Performance
                            </MDTypography>
                            <MDBox mt={0.5}>
                              <MDProgress
                                variant="gradient"
                                value={perfVal}
                                color={perfVal > 50 ? "success" : "error"}
                                sx={{
                                  width: "100%",
                                  height: 8,
                                  borderRadius: 8,
                                }}
                              />
                            </MDBox>
                            <MDBox
                              mt={0.5}
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                {perfPct}% kehadiran
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                color="text"
                              >
                                {perfVal}%
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox mt={1.25}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Tidak Mengisi
                            </MDTypography>
                            <MDBox mt={0.5}>
                              <MDProgress
                                variant="gradient"
                                value={tdkVal}
                                color={tdkVal < 50 ? "success" : "error"}
                                sx={{
                                  width: "100%",
                                  height: 8,
                                  borderRadius: 8,
                                }}
                              />
                            </MDBox>
                            <MDBox
                              mt={0.5}
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                {tdkPct}% tidak mengisi
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                color="text"
                              >
                                {tdkVal}%
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox mt={1}>
                            <MDButton
                              variant="outlined"
                              color="warning"
                              size="small"
                              onClick={() => {
                                navigate(
                                  `/pages/smartDH/kinerja-alldinas/detail/${"1"}?tAwal=${tanggalAwal}&tAkhir=${tanggalAkhir}&unitKerja=${
                                    item.unitKerja
                                  }`
                                );
                              }}
                              sx={{ minWidth: "auto", px: 1 }}
                            >
                              <Icon sx={{ fontSize: 16 }}>visibility</Icon>
                              &nbsp;Detail
                            </MDButton>
                          </MDBox>
                        </CardContent>
                      </Card>
                    );
                  })}
              </MDBox>
            ) : (
            <DataTable
              key={`kinerja-table-${listLaporPD.length}-${tanggalAwal}-${tanggalAkhir}`}
              table={{
                columns,
                rows: loading ? [] : rows,
              }}
              isSorted={false}
              entriesPerPage={25}
              showTotalEntries={true}
              noEndBorder
              canSearch={true}
              entriesOptions={[5, 10, 15, 20, 25, 50]}
            />
            )}
            {loading && !isMobile && (
              <MDBox display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default KinerjaAllDinas;
